import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Toaster } from 'react-hot-toast';
import './App.css';
import RECAPTCHA_CONFIG from './config/recaptcha';

// Components
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';

// Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminUsers from './pages/Admin/AdminUsers';
import AdminMerchandise from './pages/Admin/AdminMerchandise';
import AdminOrders from './pages/Admin/AdminOrders';
import AdminArticles from './pages/Admin/AdminArticles';
import AdminPrograms from './pages/Admin/AdminPrograms';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TermsConditions from './pages/TermsConditions';
import Contact from './pages/Contact';

// New Pages
import About from './pages/About';
import Team from './pages/Team';
import Programmes from './pages/Programmes';
import Merchandise from './pages/Merchandise';
import MemberCompetitions from './pages/MemberCompetitions';
import AdminCompetitions from './pages/Admin/AdminCompetitions';
import PaymentSuccess from './pages/PaymentSuccess';
import Articles from './pages/Articles';

function App() {
  // Global error handler for unhandled promise rejections
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection details:');
      console.error('   Reason:', event.reason);
      console.error('   Type:', typeof event.reason);
      console.error('   Message:', event.reason?.message || 'No message');
      console.error('   Stack:', event.reason?.stack || 'No stack');
      console.error('   Full event:', event);
      
      // Log additional context for null rejections
      if (event.reason === null || event.reason === undefined) {
        console.error('NULL/UNDEFINED REJECTION DETECTED');
        console.error('   This usually indicates a promise was rejected without a proper error object');
        console.error('   Check for: Promise.reject(), throw null, or async functions without proper error handling');
        console.error('   Current URL:', window.location.href);
        console.error('   User Agent:', navigator.userAgent);
      }
      
      // Prevent the default browser behavior
      event.preventDefault();
    };

    const handleError = (event) => {
      console.error('Global error:', event.error);
      console.error('   Error message:', event.error?.message);
      console.error('   Error stack:', event.error?.stack);
      console.error('   Current URL:', window.location.href);
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_CONFIG.SITE_KEY}
        scriptProps={{
          async: false,
          defer: false,
          appendTo: 'head',
          nonce: undefined,
        }}
      >
        <AuthProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <div className="min-h-screen bg-gray-50">
            <Navbar />
            <main>
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/team" element={<Team />} />
              <Route path="/programmes" element={<Programmes />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/contact" element={<Contact />} />
              
              {/* Legacy Routes - Redirect to new structure */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/terms" element={<TermsConditions />} />
              
              {/* Payment Success Routes */}
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/membership-success" element={<PaymentSuccess />} />
              <Route path="/competition-payment-success" element={<PaymentSuccess />} />
              <Route path="/program-payment-success" element={<PaymentSuccess />} />
              <Route path="/program-invoice-success" element={<PaymentSuccess />} />
              <Route path="/merchandise-payment-success" element={<PaymentSuccess />} />
              <Route path="/qr-payment-success" element={<PaymentSuccess />} />
              <Route path="/qr-membership-success" element={<PaymentSuccess />} />
              
              {/* Protected Routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } />
              <Route path="/competitions" element={
                <ProtectedRoute>
                  <MemberCompetitions />
                </ProtectedRoute>
              } />
              <Route path="/merchandise" element={
                <ProtectedRoute>
                  <Merchandise />
                </ProtectedRoute>
              } />
              
              {/* Admin Routes */}
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } />
              <Route path="/admin/users" element={
                <AdminRoute>
                  <AdminUsers />
                </AdminRoute>
              } />
              <Route path="/admin/products" element={
                <AdminRoute>
                  <AdminMerchandise />
                </AdminRoute>
              } />
              <Route path="/admin/orders" element={
                <AdminRoute>
                  <AdminOrders />
                </AdminRoute>
              } />
              <Route path="/admin/articles" element={
                <AdminRoute>
                  <AdminArticles />
                </AdminRoute>
              } />
              <Route path="/admin/programs" element={
                <AdminRoute>
                  <AdminPrograms />
                </AdminRoute>
              } />
              <Route path="/admin/competitions" element={
                <AdminRoute>
                  <AdminCompetitions />
                </AdminRoute>
              } />
            </Routes>
          </main>
          <Toaster position="top-right" />
        </div>
        </Router>
      </AuthProvider>
      </GoogleReCaptchaProvider>
  );
}

export default App;
